import { useMemo } from 'react';
import { useCatalogItems } from './catalogItems';
import type { HardwareSet } from './hardwareSets';
import type { Material } from './materialsRun';
import { surfaceKey, surfaceOf } from './materialSurface';
import { writtenPartName } from './openaiBlenderApi';
import { materialsOfPart, partNamesOf } from './partMaterialRun';
import { partCatalogId } from './partIdentity';
import { usePartsRun } from './partsRun';
import { useToolPairs } from './toolPairs';
import { materialText } from './variantLabel';

// A TOOL AND THE HARDWARE IT DRIVES, BUILT AS ONE SET — the screwdriver, the chipboard screw and the
// barrel nut it turns, in one scene, its tip engaged in the first one's recess (see
// openai_blender._driver_block). Kept, it is cut into one model per part, as every set is.
//
// It was built after its hardware, told the recess that hardware's build had declared — and the
// recess was a guess off a drawing a few pixels wide: a 1.6 mm Phillips recess, and a driver built
// to it was a stub. Built together, the tip and the recesses are cut against each other in one
// script, and the tool takes its look from its own drawing and page crops, in view.
//
// The set's key is its parts' catalog ids, like a hardware table row's; `tool` is which part of it
// is the tool, by its index in the parts list. Null for a tool that drives nothing, or where fewer
// than two of the parts have a catalog row — there would be nowhere to file a piece.
export type ToolSet = HardwareSet & { tool: number };

// EVERY TOOL'S SET, in the parts list's order — the tools subphase's dropdown (see PartsPhase).
export function useToolSets(docId: string): ToolSet[] {
  const { items } = usePartsRun(docId);
  const { rows } = useToolPairs(docId);
  const { items: catalog, loaded } = useCatalogItems();
  return useMemo(() => items.flatMap((tool, toolAt) => {
    if (!tool.tool) return [];
    const driven = [...new Set(rows.filter((r) => r.tool === String(toolAt) && r.hardware != null)
      .map((r) => Number(r.hardware)))];
    const parts = [toolAt, ...driven].flatMap((at) => {
      const p = items[at];
      const itemId = p ? partCatalogId(catalog, loaded, p) : null;
      return p && itemId != null ? [{ name: writtenPartName(p), itemId, at }] : [];
    });
    if (parts.length < 2 || parts[0].at !== toolAt) return [];
    return [{ key: parts.map((p) => p.itemId).sort((a, b) => a - b).join('-'), parts, tool: toolAt }];
  }), [items, rows, catalog, loaded]);
}

// THE HARDWARE SOME TOOL DRIVES, by index in the parts list: built in that tool's set, and so never
// built again — a joint's set starts with its kept model in the scene (see notice_3d._reused), and
// its own card makes none.
export function useToolDriven(docId: string): Set<number> {
  const { rows } = useToolPairs(docId);
  return useMemo(() => new Set(rows.flatMap((r) => (r.hardware != null ? [Number(r.hardware)] : []))), [rows]);
}

// WHAT A PART ON SEVERAL MATERIALS IS BUILT WITH: their names, and which part of it each is where
// the table says — a tool's blade and handle. The build makes each mesh of the one it is made of,
// so the table's row is what paints it (a paint goes by name). Left to name them itself, it built a
// screwdriver of "Polished steel" and "Dark grey tool plastic", which no row is called.
//
// Null for a part on one material: built bare and painted, as it was. Throws, with `noSurface`
// before the material's name, where one has not been judged — nothing is built on it.
export function partBuildMaterials(docId: string, submodel: string, at: number, noSurface: string): Material[] | null {
  const madeOf = materialsOfPart(docId, submodel, at);
  if (madeOf.length < 2) return null;
  const partNames = partNamesOf(docId, submodel, at);
  return madeOf.map((pair) => {
    const surface = surfaceOf(docId, pair);
    if (!surface.rgb || surface.roughness == null || surface.metallic == null) {
      throw new Error(`${noSurface} ${materialText(pair)}`);
    }
    return {
      name: materialText(pair), rgb: surface.rgb, roughness: surface.roughness,
      metallic: surface.metallic, fibre: surface.fibre === true,
      ...(surface.texture ? { texture: surface.texture } : {}),
      // Which part of it it is, where the table says: the build is told, not left to judge.
      ...(partNames[surfaceKey(pair)] ? { of: partNames[surfaceKey(pair)] } : {}),
    };
  });
}
